import { logger } from "@platform/browser-logger";
import { GroupMember, useForm } from "@platform/census-components";
import { useMutation, useQuery } from "@tanstack/react-query";
import axios from "axios";
import { useApi } from "@platform/api";

import { useAnalyticsEvents } from "../hooks/useAnalyticsEvents";
import useToastNotificationService from "../hooks/useToastNotificationService";

interface CensusExistsResponse {
  exists: boolean;
}

interface UseCensusDocumentReturn {
  isLoading: boolean;
  isError: boolean;
  isCensusExists: boolean;
  isDeletingCensusDocument: boolean;
  onDeleteCensusDocument: () => Promise<void>;
  formState: ReturnType<typeof useForm>["formState"];
  onChange: ReturnType<typeof useForm>["onChange"];
  onConfirm: ReturnType<typeof useForm>["onConfirm"];
  updateFormState: ReturnType<typeof useForm>["updateFormState"];
  updateFormStateValue: ReturnType<typeof useForm>["updateFormStateValue"];
  isInvalid: ReturnType<typeof useForm>["isInvalid"];
}

interface Options {
  useExperimentalCoverageClasses?: boolean;
}

function useCensusDocument(
  id?: string,
  options?: Options,
): UseCensusDocumentReturn {
  const axiosInstance = useApi();
  const { createToastNotification } = useToastNotificationService();
  const { trackCensusUpload } = useAnalyticsEvents();

  const censusExistsQuery = useQuery({
    queryKey: ["censusExists", id],
    queryFn: () =>
      axiosInstance
        .get<CensusExistsResponse>(
          `brokerportal/v1/rfp/${id}/census-document/exist`,
        )
        .then((resp) => resp.data),
    enabled: !!id,
  });

  const uploadCensusMutation = useMutation({
    mutationFn: async ({ res, file }: { res: GroupMember[]; file: Blob }) => {
      const body = new FormData();
      body.append("file", file, "census.xlsx");
      await axiosInstance.post(
        `brokerportal/v1/rfp/${id}/census-document`,
        body,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );

      await axiosInstance.patch(`brokerportal/v1/rfp/${id}`, {
        census: res,
      });

      return res;
    },
    onSuccess: () => {
      censusExistsQuery.refetch();
      trackCensusUpload();
    },
    onError: (err) => {
      const errorMessage = axios.isAxiosError(err)
        ? (err.response?.data as { errorMessage?: string })?.errorMessage
        : err.message;

      if (errorMessage) {
        logger.error(errorMessage);
      }

      createToastNotification({
        severity: "error",
        message: "An error occurred while processing the census.",
      });
    },
  });

  async function onProcessed(res: GroupMember[], file: Blob): Promise<void> {
    if (uploadCensusMutation.isPending) {
      return;
    }
    await uploadCensusMutation.mutateAsync({ res, file });
  }

  const deleteCensusDocumentMutation = useMutation({
    mutationFn: async () => {
      await axiosInstance.delete(`brokerportal/v1/rfp/${id}/census-document`);
    },
    onSuccess: () => {
      censusExistsQuery.refetch();
      createToastNotification({
        severity: "success",
        message: "Census document deleted successfully.",
      });
    },
    onError: () => {
      createToastNotification({
        severity: "error",
        message: "An error occurred while deleting the census document.",
      });
    },
  });

  const {
    formState,
    onChange,
    onConfirm,
    updateFormState,
    updateFormStateValue,
    isInvalid,
  } = useForm({
    onProcessed,
    useExperimentalCoverageClasses: options?.useExperimentalCoverageClasses,
  });

  return {
    isLoading: censusExistsQuery.isLoading,
    isError: censusExistsQuery.isError,
    isCensusExists: censusExistsQuery.data?.exists ?? false,
    isDeletingCensusDocument: deleteCensusDocumentMutation.isPending,
    onDeleteCensusDocument: deleteCensusDocumentMutation.mutateAsync,
    formState,
    onChange,
    onConfirm,
    updateFormState,
    updateFormStateValue,
    isInvalid,
  };
}

export { useCensusDocument, UseCensusDocumentReturn };
